import { useState } from "react";
import { colors } from "../../global";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartPie } from "@fortawesome/free-solid-svg-icons";

function Card({
  data,
  title,
  icon = faChartPie,
  variant = "default", // default, accent, success, warning
  trend,
  description,
  onClick,
  className = "",
}) {
  const [isHovered, setIsHovered] = useState(false);

  const getVariantStyles = () => {
    switch (variant) {
      case "accent":
        return {
          background: `linear-gradient(135deg, ${colors.accent} 0%, #FF8A8D 100%)`,
          shadowColor: colors.accent + "40",
        };
      case "success":
        return {
          background: "linear-gradient(135deg, #10B981 0%, #34D399 100%)",
          shadowColor: "#10B98140",
        };
      case "warning":
        return {
          background: "linear-gradient(135deg, #F59E0B 0%, #FCD34D 100%)",
          shadowColor: "#F59E0B40",
        };
      default:
        return {
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
          shadowColor: colors.primary + "40",
        };
    }
  };

  const styles = getVariantStyles();

  return (
    <div
      className={`relative group cursor-pointer transition-all duration-300 ease-out ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
      style={{
        transform: isHovered ? "translateY(-4px)" : "translateY(0)",
      }}
    >
      {/* Main Card */}
      <div
        className="relative overflow-hidden rounded-2xl shadow-lg transition-all duration-300 p-6"
        style={{
          background: styles.background,
          boxShadow: isHovered
            ? `0 20px 40px -12px ${styles.shadowColor}, 0 8px 16px -8px ${styles.shadowColor}`
            : `0 8px 25px -8px ${styles.shadowColor}`,
        }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-4 -right-4 w-24 h-24 rounded-full bg-white"></div>
          <div className="absolute -bottom-2 -left-2 w-16 h-16 rounded-full bg-white"></div>
        </div>

        {/* Content */}
        <div className="relative z-10">
          {/* Header with icon */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <FontAwesomeIcon
                  icon={icon}
                  className="w-6 h-6 text-white opacity-80"
                />
                <h3 className="text-white text-sm font-medium uppercase tracking-wider opacity-90">
                  {title}
                </h3>
              </div>
            </div>
          </div>

          {/* Data Display */}
          <div className="mb-3">
            <div className="flex items-baseline gap-2">
              <span className="text-4xl font-bold text-white leading-none">
                {data}
              </span>
              {trend && (
                <div
                  className={`flex items-center gap-1 text-sm font-medium ${
                    trend.type === "up"
                      ? "text-green-200"
                      : trend.type === "down"
                      ? "text-red-200"
                      : "text-white"
                  }`}
                >
                  {trend.type === "up" && (
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                  {trend.type === "down" && (
                    <svg
                      className="w-4 h-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                  <span>{trend.value}</span>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          {description && (
            <p className="text-white text-sm opacity-80 leading-relaxed">
              {description}
            </p>
          )}
        </div>

        {/* Decorative Large Icon */}
        <FontAwesomeIcon
          icon={icon}
          className="absolute -bottom-2 -right-2 text-white opacity-20 text-5xl transition-all duration-300"
          style={{
            transform: isHovered
              ? "scale(1.1) rotate(5deg)"
              : "scale(1) rotate(0deg)",
          }}
        />

        {/* Hover Indicator */}
        <div
          className={`absolute bottom-0 left-0 right-0 h-1 bg-white transition-all duration-300 ${
            isHovered ? "opacity-30" : "opacity-0"
          }`}
        ></div>
      </div>
    </div>
  );
}

export default Card;
